import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { userManagementService } from '../../services/userManagementService';
import { normalizeEmail } from '../../utils/normalizeEmail';
import type { UserAccessLevel } from '../../services/authService';

interface AdminUserCreateProps {
  onSuccess: (userId: string, userEmail: string) => void;
  onError: (error: string) => void;
}

const AdminUserCreate: React.FC<AdminUserCreateProps> = ({
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [accessLevel, setAccessLevel] = useState<UserAccessLevel>('support');
  const [emailError, setEmailError] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);

  // Auto-generate username from email when email changes
  useEffect(() => {
    if (email && email.includes('@')) {
      const generatedUsername = userManagementService.generateUsernameFromEmail(email);
      setUsername(generatedUsername);
    } else {
      setUsername('');
    }
  }, [email]);

  // Check email availability
  useEffect(() => {
    if (email && email.includes('@')) {
      const checkEmail = async () => {
        setEmailChecking(true);
        setEmailError('');
        try {
          const exists = await userManagementService.checkEmailExists(email);
          if (exists) {
            setEmailError('This email is already registered');
          }
        } catch (error) {
          console.error('Error checking email:', error);
        } finally {
          setEmailChecking(false);
        }
      };

      const timer = setTimeout(checkEmail, 500);
      return () => clearTimeout(timer);
    } else {
      setEmailError('');
    }
  }, [email]);

  const handleCreate = async () => {
    // Validation
    if (!email || !email.includes('@')) {
      onError('Please enter a valid email address');
      return;
    }

    if (!username || username.length < 3) {
      onError('Username must be at least 3 characters');
      return;
    }

    if (emailError) {
      onError('Please fix the email error before proceeding');
      return;
    }

    if (!accessLevel || (accessLevel !== 'support' && accessLevel !== 'admin' && accessLevel !== 'all_access')) {
      onError('Please select a valid access level');
      return;
    }

    try {
      setLoading(true);
      onError('');

      const normalizedEmail = normalizeEmail(email);

      // Create client user (passwordless) with specified access level
      const userData = await userManagementService.createClientUser({
        email: normalizedEmail,
        username,
        // No password - user will set it via invitation email
        access_level: accessLevel,
      });

      // No customer assignments needed for admin/support/all_access users
      onSuccess(userData.id, normalizedEmail);
    } catch (error: any) {
      console.error('Error creating admin user:', error);
      onError(error.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create Admin/Support/All_Access User
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create a user account with elevated access. These users do not require customer assignments.
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Admin, Support, and All_Access users have system-wide access and do not need to be assigned to specific customers. 
            The user will receive a password reset email to set their password.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError || (emailChecking ? 'Checking availability...' : 'Enter the user\'s email address')}
            disabled={loading || emailChecking}
            required
            InputProps={{
              endAdornment: emailChecking ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />

          <FormControl fullWidth required>
            <InputLabel id="access-level-label">Access Level</InputLabel>
            <Select
              labelId="access-level-label"
              id="access-level-select"
              value={accessLevel}
              label="Access Level"
              onChange={(e) => setAccessLevel(e.target.value as UserAccessLevel)}
              disabled={loading}
            >
              <MenuItem value="support">Support</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="all_access">All Access</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
            helperText="Username for login (auto-generated from email, can be edited)"
            required
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleCreate}
              disabled={loading || !!emailError || emailChecking || !email || !username || !accessLevel}
              sx={{ minWidth: 200 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminUserCreate;
